import {Decoder} from 'json-bouncer'
import * as R from 'ramda'
import * as PrismaRequestFilteringState
    from '../../Components/PrismaRequestFiltering/Model/PrismaRequestFilteringState'
import {PrismaRequestFilteringFeedback} from '../../Components/PrismaRequestFiltering/PrismaRequestFilteringFeedback'
import * as PrismaRequestFilteringReducer
    from '../../Components/PrismaRequestFiltering/PrismaRequestFilteringReducer'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import {decodeHttpResponse, HttpError} from '../../Data/Http'
import {HttpRequest} from '../../Data/HttpRequest'
import {update} from '../../Data/ObjectHelper'
import * as PageSection from '../../Data/PageSection'
import {Paginated, paginatedDecoder} from '../../Data/Paginated'
import * as Pagination from '../../Data/Pagination'
import {Result} from '../../Data/Result'
import {Session} from '../../Data/Session'
import * as PrismaRequestWithOffersForList from '../../Entities/PrismaRequestWithOffersForList'
import * as PrismaRequestOfferResource
    from '../../Request/PrismaRequestOfferResource'
import {PrismaRequestOverviewAction} from './PrismaRequestOverviewActions'
import {
    PrismaRequestOverviewFeedback,
    PrismaRequestOverviewRequestIdentifier
} from './PrismaRequestOverviewFeedback'
import {PrismaRequestOverviewState} from './PrismaRequestOverviewState'

type PrismaRequestOverviewLoop =
    FeedbackLoop<PrismaRequestOverviewState, PrismaRequestOverviewFeedback>

const initialItemsPerPage = 10

export function activate(session: Session): HttpRequest {
    return PrismaRequestOfferResource.listWithOffers(
        session.manufacturer.id,
        [],
        {offset: 0, limit: initialItemsPerPage},
    )
}

export function initialDecoder(): Decoder<Paginated<PrismaRequestWithOffersForList.M>> {
    return paginatedDecoder(PrismaRequestWithOffersForList.decoder())
}

export function init(
    prismaRequestsWithOffers: Paginated<PrismaRequestWithOffersForList.M>,
): PrismaRequestOverviewState {
    return {
        prismaRequestFiltering: PrismaRequestFilteringState.init([]),
        prismaRequestsWithOffersForList:
            Pagination.fromPaginated(
                {itemsPerPage: initialItemsPerPage, currentPage: 1},
                prismaRequestsWithOffers,
            ),
        loading: false,
    }
}

export function prismaRequestOverviewReducer(
    session: Session,
    state: PrismaRequestOverviewState,
    action: PrismaRequestOverviewAction,
): PrismaRequestOverviewLoop {
    switch (action.type) {
        case 'MenuButtonClicked':
            return {
                state,
                feedback: [{type: 'OpenDrawer'}],
            }
        case 'PaginationNavigation':
            return requestPrismaRequestOffers(
                session,
                {
                    state: update(
                        {
                            prismaRequestsWithOffersForList:
                                Pagination.navigate(
                                    action.navigation,
                                    state.prismaRequestsWithOffersForList,
                                ),
                        },
                        state,
                    ),
                    feedback: []
                }
            )
        case 'ItemsPerPageChanged':
            return requestPrismaRequestOffers(
                session,
                {
                    state: update(
                        {
                            prismaRequestsWithOffersForList:
                                Pagination.setItemsPerPage(
                                    action.value,
                                    state.prismaRequestsWithOffersForList,
                                ),
                        },
                        state,
                    ),
                    feedback: []
                }
            )
        case 'PrismaRequestOfferListResponse':
            const result =
                decodeHttpResponse(
                    paginatedDecoder(PrismaRequestWithOffersForList.decoder()),
                    action.result,
                )
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    prismaRequestsWithOffersForList:
                                        Pagination.setData(
                                            result.value,
                                            state.prismaRequestsWithOffersForList,
                                        ),
                                    loading: false,
                                },
                                state,
                            ),
                            feedback: [],
                        }
                    case 'Err':
                        // tslint:disable-next-line no-console
                        console.log(result.error)
                        return {
                            state: update({loading: false}, state),
                            feedback: [],
                        }
                }
            })()
        case 'PrismaRequestFilteringModuleAction':
            const
                prismaRequestFilteringLoop =
                    PrismaRequestFilteringReducer.reducer(
                        state.prismaRequestFiltering,
                        action.subAction,
                    ),
                wrappedLoop = {
                    state: update(
                        {
                            prismaRequestFiltering:
                            prismaRequestFilteringLoop.state,
                        },
                        state,
                    ),
                    feedback: [],
                }
            return R.reduce(
                (
                    previousLoop: PrismaRequestOverviewLoop,
                    orderFilterFeedback: PrismaRequestFilteringFeedback,
                ): PrismaRequestOverviewLoop => {
                    switch (orderFilterFeedback.type) {
                        case 'SendRequest':
                            return {
                                state: previousLoop.state,
                                feedback:
                                    R.append<PrismaRequestOverviewFeedback>(
                                        {
                                            type: 'SendRequest',
                                            identifier: {
                                                type: 'PrismaRequestFilteringRequest',
                                                subIdentifier:
                                                orderFilterFeedback.identifier,
                                            },
                                            httpRequest: orderFilterFeedback.httpRequest,
                                        },
                                        previousLoop.feedback,
                                    ),
                            }
                        case 'FiltersChanged':
                            return requestPrismaRequestOffers(
                                session,
                                previousLoop,
                            )
                    }
                },
                wrappedLoop,
                prismaRequestFilteringLoop.feedback,
            )
    }
}

function requestPrismaRequestOffers(
    session: Session,
    {state, feedback}: PrismaRequestOverviewLoop
): PrismaRequestOverviewLoop {
    return {
        state: update({loading: true}, state),
        feedback: R.append<PrismaRequestOverviewFeedback>(
            {
                type: 'SendRequest',
                identifier: {type: 'PrismaRequestOfferList'},
                httpRequest: PrismaRequestOfferResource.listWithOffers(
                    session.manufacturer.id,
                    state.prismaRequestFiltering.activeFilters,
                    PageSection.toOffsetLimit(
                        state.prismaRequestsWithOffersForList.pageSection,
                    ),
                ),
            },
            feedback,
        ),
    }
}

export function httpResponseAction(
    identifier: PrismaRequestOverviewRequestIdentifier,
    result: Result<HttpError, string>
): PrismaRequestOverviewAction {
    switch (identifier.type) {
        case 'PrismaRequestOfferList':
            return {
                type: 'PrismaRequestOfferListResponse',
                result,
            }
        case 'PrismaRequestFilteringRequest':
            return {
                type: 'PrismaRequestFilteringModuleAction',
                subAction:
                    PrismaRequestFilteringReducer.httpResponseAction(
                        identifier.subIdentifier,
                        result,
                    ),
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/PrismaRequestOverview/PrismaRequestOverviewReducer.ts